import { supabase, type EnvioCampana, type EstadoEnvioCampana } from '../lib/supabase';
import type { MedioPago } from '../types/comprobante';
import type { Json } from '../types/database';
import type {
  AsignacionSegundoTramo, EnvioCampanaAsignacion,
  EnvioCampanaHistorial, OwnerFondoCampana, ResultadoEnvioCampana,
} from '../types/fondosCampana';
import { lanzarErrorOperacionCampana, validarResultadoEnvio } from '../utils/fondosCampanaRpc';

interface EnvioCampanaRaw extends EnvioCampana {
  readonly campana?: { readonly nombre: string; readonly nivel_origen: string } | null;
  readonly campana_master?: { readonly nombre: string } | null;
  readonly lumisial?: { readonly nombre: string; readonly ciudad: string | null;
    readonly provincia_eclesiastica_id: string; readonly diocesis_id: string } | null;
  readonly origen_diocesis?: { readonly nombre: string } | null;
  readonly destino_diocesis?: { readonly nombre: string } | null;
  readonly destino_provincia?: { readonly nombre: string } | null;
}

export interface EnvioCampanaConDetalles extends EnvioCampana {
  readonly campana_nombre?: string;
  readonly lumisial_nombre?: string;
  readonly lumisial_ciudad?: string;
  readonly lumisial_provincia_eclesiastica_id?: string;
  readonly lumisial_diocesis_id?: string;
  readonly campana_nivel_origen?: string;
  readonly campana_master_nombre?: string;
  readonly origen_diocesis_nombre?: string;
  readonly destino_diocesis_nombre?: string;
  readonly destino_provincia_nombre?: string;
}
export interface FiltrosEnviosCampana {
  readonly campanaMasterId?: string;
  readonly estado?: EstadoEnvioCampana;
}
export interface OrigenDisponibleCampana extends EnvioCampanaConDetalles {
  readonly asignadoVigente: number;
  readonly remanente: number;
}
/** Comprobante de una transferencia entre juntas: se exige al crear, sin "subir después". */
export interface RespaldoTransferencia {
  readonly medioPago: MedioPago | null;
  readonly comprobanteId: string | null;
}
export interface CrearPrimerTramoParams {
  readonly campanaId: string; readonly monto: number; readonly fechaEnvio: string;
  readonly descripcion: string | null; readonly solicitudId: string;
  readonly respaldo: RespaldoTransferencia;
}
export interface CrearSegundoTramoParams {
  readonly campanaMasterId: string; readonly diocesisId: string;
  readonly fechaEnvio: string; readonly descripcion: string | null;
  readonly asignaciones: readonly AsignacionSegundoTramo[]; readonly solicitudId: string;
  readonly respaldo: RespaldoTransferencia;
}
export interface ResolverEnvioParams {
  readonly envioId: string; readonly revision: number;
  readonly decision: 'confirmado' | 'observado'; readonly nota: string | null;
  readonly solicitudId: string;
}
export interface CorregirEnvioParams {
  readonly envioId: string; readonly revision: number; readonly descripcion: string | null;
  readonly motivo: string; readonly solicitudId: string;
  /** Sin comprobante nuevo se conserva el actual. */
  readonly respaldo?: RespaldoTransferencia;
}
export interface CerrarSinTransferenciaParams {
  readonly envioId: string; readonly revision: number; readonly noSeEjecuto: boolean;
  readonly motivo: string; readonly solicitudId: string;
}

const SELECT_DETALLES = `*,
  campana:campana_id(nombre,nivel_origen),campana_master:campana_master_id(nombre),
  lumisial:lumisial_id(nombre,ciudad,provincia_eclesiastica_id,diocesis_id),
  origen_diocesis:origen_diocesis_id(nombre),destino_diocesis:destino_diocesis_id(nombre),
  destino_provincia:destino_provincia_eclesiastica_id(nombre),
  comprobante:comprobantes!envios_campana_comprobante_id_fkey(id,tipo_archivo,rutas,nombre_original)`;

const SELECT_CON_COMPROBANTE =
  '*,comprobante:comprobantes!envios_campana_comprobante_id_fkey(id,tipo_archivo,rutas,nombre_original)';

function mapearEnvioConDetalles(e: EnvioCampanaRaw): EnvioCampanaConDetalles {
  return { ...e, campana_nombre: e.campana?.nombre, lumisial_nombre: e.lumisial?.nombre,
    lumisial_ciudad: e.lumisial?.ciudad ?? undefined,
    lumisial_provincia_eclesiastica_id: e.lumisial?.provincia_eclesiastica_id,
    lumisial_diocesis_id: e.lumisial?.diocesis_id, campana_nivel_origen: e.campana?.nivel_origen,
    campana_master_nombre: e.campana_master?.nombre, origen_diocesis_nombre: e.origen_diocesis?.nombre,
    destino_diocesis_nombre: e.destino_diocesis?.nombre, destino_provincia_nombre: e.destino_provincia?.nombre };
}

type OperacionEnvio = 'crear_envio_campana_primer_tramo' | 'crear_envio_campana_segundo_tramo'
  | 'resolver_envio_campana' | 'corregir_rendicion_envio_campana'
  | 'cerrar_envio_campana_sin_transferencia';

async function ejecutarOperacionEnvio(nombre: OperacionEnvio, argumentos: Readonly<Record<string, Json>>): Promise<unknown> {
  try {
    const { data, error } = await supabase.rpc(nombre, argumentos);
    if (error) lanzarErrorOperacionCampana(error);
    return data;
  } catch (error: unknown) {
    lanzarErrorOperacionCampana(error);
  }
}

// Las lecturas de procedencia se paginan para no omitir reservas por el límite del servidor.
async function leerProcedencia<T>(pagina: (desde: number, hasta: number) =>
  PromiseLike<{ readonly data: unknown; readonly error: unknown }>): Promise<T[]> {
  const resultado: T[] = [];
  const tamano = 500;
  for (let desde = 0; ; desde += tamano) {
    const { data, error } = await pagina(desde, desde + tamano - 1);
    if (error) throw error;
    if (!Array.isArray(data)) throw new Error('No se recibió la procedencia completa del envío.');
    resultado.push(...data as T[]);
    if (data.length < tamano) return resultado;
  }
}

function calcularRemanente(origen: EnvioCampanaRaw, asignaciones: readonly EnvioCampanaAsignacion[],
  segundos: readonly EnvioCampana[]): OrigenDisponibleCampana {
  let usadoCentavos = 0;
  for (const a of asignaciones.filter(fila => fila.envio_primer_tramo_id === origen.id)) {
    const segundo = segundos.find(e => e.id === a.envio_segundo_tramo_id);
    if (!segundo) throw new Error('La procedencia del segundo tramo no se pudo consultar.');
    if (segundo.estado === 'cerrado_sin_transferencia') continue;
    usadoCentavos += Math.round(a.monto_asignado * 100);
  }
  const remanente = (Math.round(origen.monto * 100) - usadoCentavos) / 100;
  if (!Number.isFinite(remanente) || remanente < 0 || usadoCentavos < 0) {
    throw new Error('El remanente cambió o requiere conciliación. Vuelve a cargarlo.');
  }
  return { ...mapearEnvioConDetalles(origen), asignadoVigente: usadoCentavos / 100, remanente };
}

export const enviosCampanaService = {
  async getByCampana(campanaId: string): Promise<EnvioCampana[]> {
    return leerProcedencia<EnvioCampana>((desde,hasta) => supabase.from('envios_campana')
      .select('*').eq('campana_id',campanaId).order('created_at',{ ascending:false })
      .order('id').range(desde,hasta));
  },
  async getById(id: string): Promise<EnvioCampana | null> {
    const { data,error } = await supabase.from('envios_campana').select(SELECT_CON_COMPROBANTE).eq('id',id).maybeSingle();
    if (error) throw error;
    // Supabase tipa el embebido del comprobante como arreglo genérico; el shape real es EnvioCampana.
    return data as unknown as EnvioCampana | null;
  },
  async getPendientesParaAdmin(owner: OwnerFondoCampana, campanaMasterId?: string): Promise<EnvioCampanaConDetalles[]> {
    return this.getTodosParaAdmin(owner,{ estado:'pendiente',campanaMasterId });
  },
  async getTodosParaAdmin(owner: OwnerFondoCampana, filtros: FiltrosEnviosCampana = {}): Promise<EnvioCampanaConDetalles[]> {
    if (owner.tipo === 'lumisial') throw new Error('La recepción de envíos corresponde a JAD o JAN.');
    const destino = owner.tipo === 'diocesis' ? 'destino_diocesis_id' : 'destino_provincia_eclesiastica_id';
    const data = await leerProcedencia<EnvioCampanaRaw>((desde,hasta) => {
      let query = supabase.from('envios_campana').select(SELECT_DETALLES).eq(destino,owner.id);
      if (filtros.estado) query = query.eq('estado',filtros.estado);
      if (filtros.campanaMasterId) query = query.eq('campana_master_id',filtros.campanaMasterId);
      return query.order('created_at',{ ascending:false }).order('id').range(desde,hasta);
    });
    return data.map(mapearEnvioConDetalles);
  },
  async getPorOrigen(owner: OwnerFondoCampana, filtros: FiltrosEnviosCampana = {}): Promise<EnvioCampanaConDetalles[]> {
    if (owner.tipo === 'provincia') return [];
    const origen = owner.tipo === 'lumisial' ? 'lumisial_id' : 'origen_diocesis_id';
    const data = await leerProcedencia<EnvioCampanaRaw>((desde,hasta) => {
      let query = supabase.from('envios_campana').select(SELECT_DETALLES).eq(origen,owner.id);
      if (filtros.estado) query = query.eq('estado',filtros.estado);
      if (filtros.campanaMasterId) query = query.eq('campana_master_id',filtros.campanaMasterId);
      return query.order('created_at',{ ascending:false }).order('id').range(desde,hasta);
    });
    return data.map(mapearEnvioConDetalles);
  },
  async getHistorial(envioId: string): Promise<EnvioCampanaHistorial[]> {
    return leerProcedencia<EnvioCampanaHistorial>((desde,hasta) => supabase.from('envios_campana_historial')
      .select('*').eq('envio_id',envioId).order('created_at').order('solicitud_id').range(desde,hasta));
  },
  async getBySolicitud(solicitudId: string): Promise<EnvioCampanaHistorial | null> {
    const { data: sesion,error: errorSesion } = await supabase.auth.getUser();
    if (errorSesion) throw errorSesion;
    if (!sesion.user) throw new Error('Sesión requerida para consultar la solicitud.');
    const { data,error } = await supabase.from('envios_campana_historial').select('*')
      .eq('solicitud_id',solicitudId).eq('actor_id',sesion.user.id).maybeSingle();
    if (error) throw error;
    return data;
  },
  async getAsignaciones(envioSegundoTramoId: string): Promise<EnvioCampanaAsignacion[]> {
    return leerProcedencia<EnvioCampanaAsignacion>((desde,hasta) => supabase.from('envios_campana_asignaciones')
      .select('*').eq('envio_segundo_tramo_id',envioSegundoTramoId)
      .order('envio_primer_tramo_id').range(desde,hasta));
  },
  /** Estimación para seleccionar procedencia; la RPC recalcula bajo bloqueo. */
  async getOrigenesDisponibles(campanaMasterId: string, diocesisId: string): Promise<OrigenDisponibleCampana[]> {
    const origenes = await leerProcedencia<EnvioCampanaRaw>((desde,hasta) => supabase.from('envios_campana')
      .select(SELECT_DETALLES).eq('campana_master_id',campanaMasterId)
      .eq('destino_diocesis_id',diocesisId).eq('estado','confirmado').not('lumisial_id','is',null)
      .order('id').range(desde,hasta));
    if (origenes.length === 0) return [];
    const primerosIds = origenes.map(e => e.id);
    const asignaciones = await leerProcedencia<EnvioCampanaAsignacion>((desde,hasta) =>
      supabase.from('envios_campana_asignaciones').select('*').in('envio_primer_tramo_id',primerosIds)
        .order('envio_primer_tramo_id').order('envio_segundo_tramo_id').range(desde,hasta));
    const segundosIds = [...new Set(asignaciones.map(a => a.envio_segundo_tramo_id))];
    const segundos = segundosIds.length === 0 ? [] : await leerProcedencia<EnvioCampana>((desde,hasta) =>
      supabase.from('envios_campana').select('*').in('id',segundosIds)
        .eq('origen_diocesis_id',diocesisId).eq('campana_master_id',campanaMasterId).order('id').range(desde,hasta));
    return origenes.map(e => calcularRemanente(e,asignaciones,segundos))
      .filter(e => e.remanente > 0);
  },
  async crearPrimerTramo(params: CrearPrimerTramoParams): Promise<ResultadoEnvioCampana> {
    return validarResultadoEnvio(await ejecutarOperacionEnvio('crear_envio_campana_primer_tramo',{
      p_campana_id:params.campanaId,p_monto:params.monto,p_fecha_envio:params.fechaEnvio,
      p_descripcion:params.descripcion,p_solicitud_id:params.solicitudId,
      p_medio_pago:params.respaldo.medioPago,p_comprobante_id:params.respaldo.comprobanteId,
    }));
  },
  async crearSegundoTramo(params: CrearSegundoTramoParams): Promise<ResultadoEnvioCampana> {
    return validarResultadoEnvio(await ejecutarOperacionEnvio('crear_envio_campana_segundo_tramo',{
      p_campana_master_id:params.campanaMasterId,p_diocesis_id:params.diocesisId,
      p_fecha_envio:params.fechaEnvio,p_descripcion:params.descripcion,p_solicitud_id:params.solicitudId,
      p_asignaciones:params.asignaciones.map(a => ({ envio_primer_tramo_id:a.envioPrimerTramoId,monto:a.monto })),
      p_medio_pago:params.respaldo.medioPago,p_comprobante_id:params.respaldo.comprobanteId,
    }));
  },
  async resolver(params: ResolverEnvioParams): Promise<ResultadoEnvioCampana> {
    return validarResultadoEnvio(await ejecutarOperacionEnvio('resolver_envio_campana',{
      p_envio_id:params.envioId,p_revision:params.revision,p_decision:params.decision,
      p_nota:params.nota,p_solicitud_id:params.solicitudId,
    }));
  },
  async corregir(params: CorregirEnvioParams): Promise<ResultadoEnvioCampana> {
    return validarResultadoEnvio(await ejecutarOperacionEnvio('corregir_rendicion_envio_campana',{
      p_envio_id:params.envioId,p_revision:params.revision,p_descripcion:params.descripcion,
      p_motivo:params.motivo,p_solicitud_id:params.solicitudId,
      p_medio_pago:params.respaldo?.medioPago ?? null,p_comprobante_id:params.respaldo?.comprobanteId ?? null,
    }));
  },
  async cerrarSinTransferencia(params: CerrarSinTransferenciaParams): Promise<ResultadoEnvioCampana> {
    return validarResultadoEnvio(await ejecutarOperacionEnvio('cerrar_envio_campana_sin_transferencia',{
      p_envio_id:params.envioId,p_revision:params.revision,p_declara_no_transferido:params.noSeEjecuto,
      p_motivo:params.motivo,p_solicitud_id:params.solicitudId,
    }));
  },
  async contarPendientesPorCampana(campanaId: string): Promise<number> {
    const { count,error } = await supabase.from('envios_campana').select('*',{ count:'exact',head:true })
      .eq('campana_id',campanaId).eq('estado','pendiente');
    if (error) throw error;
    if (typeof count !== 'number' || !Number.isInteger(count) || count < 0) {
      throw new Error('No se recibió el conteo de envíos pendientes.');
    }
    return count;
  },
};
